/**
 * Typography System Configuration
 * Establishes consistent font families, weights, sizes, and spacing across the application
 */

export const typography = {
  // Font Family
  fontFamily: {
    primary: ['Amazon Ember', 'Arial', 'sans-serif'],
    heading: ['Amazon Ember Modern', 'Amazon Ember', 'Arial', 'sans-serif'],
    secondary: ['Georgia', 'serif'],
    mono: ['Fira Code', 'Monaco', 'Consolas', 'monospace']
  },

  // Font Weights
  fontWeight: {
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
    heading: '600',
  },

  // Font Sizes (rem units for scalability)
  fontSize: {
    xxs: '0.65rem',    // 11px
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem',    // 48px
    '6xl': '3.75rem', // 60px
    '7xl': '4.5rem',  // 72px
    '8xl': '6rem',    // 96px
    '9xl': '8rem',    // 128px
    // Amazon Design System specific sizes
    'display-large': '3.5rem',   // 56px
    'display-medium': '2.75rem', // 44px
    'display-small': '2.25rem',  // 36px
    'headline-large': '2rem',    // 32px
    'headline-medium': '1.75rem', // 28px
    'headline-small': '1.5rem',  // 24px
    'title-large': '1.375rem',   // 22px
    'title-medium': '1rem',      // 16px
    'title-small': '0.875rem',   // 14px
    'body-large': '1rem',        // 16px
    'body-medium': '0.875rem',   // 14px
    'body-small': '0.75rem',     // 12px
    'label-large': '0.875rem',   // 14px
    'label-medium': '0.75rem',   // 12px
    'label-small': '0.6875rem'   // 11px
  },

  // Line Heights
  lineHeight: {
    tight: '1.25',
    normal: '1.5',
    relaxed: '1.625',
    loose: '2',
  },

  // Letter Spacing
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
};

/**
 * Typography Utility Classes
 * Pre-defined combinations for common use cases
 */
export const typographyClasses = {
  // Headings
  heading: {
    h1: 'text-[var(--font-size-3xl)] font-[var(--font-weight-heading)] font-[var(--font-family-heading)] leading-tight text-gray-900',
    h2: 'text-[var(--font-size-2xl)] font-[var(--font-weight-heading)] font-[var(--font-family-heading)] leading-tight text-gray-900',
    h3: 'text-[var(--font-size-xl)] font-[var(--font-weight-heading)] font-[var(--font-family-heading)] leading-tight text-gray-900',
    h4: 'text-[var(--font-size-lg)] font-[var(--font-weight-heading)] font-[var(--font-family-heading)] leading-normal text-gray-900',
    h5: 'text-[var(--font-size-base)] font-[var(--font-weight-heading)] font-[var(--font-family-heading)] leading-normal text-gray-900',
    h6: 'text-[var(--font-size-sm)] font-[var(--font-weight-heading)] font-[var(--font-family-heading)] leading-normal text-gray-900',
  },

  // Body Text
  body: {
    large: 'text-lg font-normal leading-relaxed text-gray-700',
    base: 'text-base font-normal leading-normal text-gray-700',
    small: 'text-sm font-normal leading-normal text-gray-600',
    xs: 'text-xs font-normal leading-normal text-gray-500',
  },

  // Button Text
  button: {
    primary: 'text-sm font-medium leading-normal',
    secondary: 'text-sm font-medium leading-normal',
    small: 'text-xs font-medium leading-normal',
    large: 'text-base font-medium leading-normal',
  },

  // Labels and Captions
  label: {
    primary: 'text-sm font-medium leading-normal text-gray-900',
    secondary: 'text-xs font-medium leading-normal text-gray-700',
    caption: 'text-xs font-normal leading-normal text-gray-500',
    uppercase: 'text-xs font-medium leading-normal text-gray-500 uppercase tracking-wide',
  },

  // Interactive Elements
  interactive: {
    link: 'text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-[var(--info-color)] hover:text-[var(--info-color)] underline-offset-4 hover:underline',
    navItem: 'text-sm font-medium text-gray-700 hover:text-gray-900',
    tabItem: 'text-sm font-medium text-gray-500 hover:text-gray-700',
    activeTab: 'text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-[var(--info-color)]',
  },

  // Status and Feedback
  status: {
    success: 'text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-[var(--success-color)]',
    warning: 'text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-[var(--warning-color)]',
    error: 'text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-[var(--error-color)]',
    info: 'text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-[var(--info-color)]',
  },
};

/**
 * Button Typography Specifications
 * Consistent typography for all button elements
 */
export const buttonTypography = {
  // Primary buttons
  primary: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    lineHeight: typography.lineHeight.normal,
    letterSpacing: typography.letterSpacing.normal,
    className: 'text-sm font-medium leading-normal',
  },

  // Secondary buttons
  secondary: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    lineHeight: typography.lineHeight.normal,
    letterSpacing: typography.letterSpacing.normal,
    className: 'text-sm font-medium leading-normal',
  },

  // Small buttons
  small: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
    lineHeight: typography.lineHeight.normal,
    letterSpacing: typography.letterSpacing.normal,
    className: 'text-xs font-medium leading-normal',
  },

  // Large buttons
  large: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    lineHeight: typography.lineHeight.normal,
    letterSpacing: typography.letterSpacing.normal,
    className: 'text-base font-medium leading-normal',
  },
};

/**
 * Span Element Typography
 * Specific typography for span elements within buttons
 */
export const spanTypography = {
  // Button spans
  buttonSpan: {
    primary: 'font-medium text-sm leading-normal',
    secondary: 'font-medium text-sm leading-normal',
    small: 'font-medium text-xs leading-normal',
    large: 'font-medium text-base leading-normal',
  },

  // Label spans
  labelSpan: {
    primary: 'font-medium text-sm text-gray-900',
    secondary: 'font-medium text-xs text-gray-700',
    caption: 'font-normal text-xs text-gray-500',
  },

  // Badge spans
  badgeSpan: {
    default: 'font-medium text-xs leading-normal',
    small: 'font-medium text-xs leading-tight',
    large: 'font-medium text-sm leading-normal',
  },
};

/**
 * Typography Utility Functions
 */
export const getTypographyClass = (category: keyof typeof typographyClasses, variant: string) => {
  return typographyClasses[category]?.[variant as keyof typeof typographyClasses[typeof category]] || '';
};

export const getButtonTypography = (size: keyof typeof buttonTypography = 'primary') => {
  return buttonTypography[size];
};

export const getSpanTypography = (type: keyof typeof spanTypography, variant: string) => {
  return spanTypography[type]?.[variant as keyof typeof spanTypography[typeof type]] || '';
};

/**
 * CSS Custom Properties for Typography
 * Can be used in CSS-in-JS or custom CSS
 */
export const typographyCSSVars = {
  '--font-family-primary': typography.fontFamily.primary.join(', '),
  '--font-family-heading': typography.fontFamily.heading.join(', '),
  '--font-family-mono': typography.fontFamily.mono.join(', '),
  '--font-weight-light': typography.fontWeight.light,
  '--font-weight-normal': typography.fontWeight.normal,
  '--font-weight-medium': typography.fontWeight.medium,
  '--font-weight-semibold': typography.fontWeight.semibold,
  '--font-weight-bold': typography.fontWeight.bold,
  '--font-size-xxs': typography.fontSize.xxs,
  '--font-size-xs': typography.fontSize.xs,
  '--font-size-sm': typography.fontSize.sm,
  '--font-size-base': typography.fontSize.base,
  '--font-size-lg': typography.fontSize.lg,
  '--font-size-xl': typography.fontSize.xl,
  '--font-size-2xl': typography.fontSize['2xl'],
  '--font-size-3xl': typography.fontSize['3xl'],
  '--font-size-4xl': typography.fontSize['4xl'],
  '--line-height-tight': typography.lineHeight.tight,
  '--line-height-normal': typography.lineHeight.normal,
  '--line-height-relaxed': typography.lineHeight.relaxed,
};

export default typography;